import React, { useCallback } from 'react';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import { useArchitectureGenerator } from '../../hooks/useArchitectureGenerator';
import { Upload, Layers, Ruler, ArrowRight, FileImage, Sparkles, Loader2 } from 'lucide-react';

const PlanToElevationWizard: React.FC = () => {
    const { activeTask, updateTaskData, nextStep, isProcessing } = useArchitectureStore();
    const { generate } = useArchitectureGenerator();

    if (!activeTask || activeTask.type !== 'PLAN_TO_ELEVATION') return null;

    const { step, data } = activeTask;

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'groundFloor' | 'typicalFloor') => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    updateTaskData({ [field]: event.target.result as string });
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSpecChange = (field: string, value: string) => {
        updateTaskData({ [field]: Number(value) });
    };

    // LOADING STATE
    if (isProcessing) {
        return (
            <div className="flex flex-col items-center justify-center w-full h-full p-10 animate-in fade-in">
                <Loader2 className="w-16 h-16 text-indigo-500 animate-spin mb-6" />
                <h2 className="text-2xl font-bold text-zinc-100">Generating Elevation...</h2>
                <p className="text-stone-500 mt-2">Analysing floor plans and constructing building mass...</p>
            </div>
        );
    }

    // --- RENDER STEPS ---

    // 1. Upload Ground Floor
    if (step === 0) {
        return (
            <div className="flex flex-col items-center justify-center w-full h-full p-8 pb-40 animate-in fade-in">
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-zinc-100">Upload Ground Floor Plan</h2>
                    <p className="text-stone-500">This will be the base of your building.</p>
                </div>

                <div className="relative w-full max-w-lg aspect-video border-2 border-dashed border-stone-700 bg-stone-900/30 rounded-2xl flex flex-col items-center justify-center hover:border-indigo-500/50 transition-colors group">
                    {data.groundFloor ? (
                        <img src={data.groundFloor} alt="Ground Floor" className="w-full h-full object-contain p-2" />
                    ) : (
                        <>
                            <FileImage className="w-12 h-12 text-stone-600 group-hover:text-stone-400 mb-4 transition-colors" />
                            <p className="text-stone-500 text-sm">Drag & drop or Click to upload</p>
                        </>
                    )}
                    <input
                        type="file"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        onChange={(e) => handleFileUpload(e, 'groundFloor')}
                        accept="image/*"
                    />
                </div>

                {data.groundFloor && (
                    <button onClick={nextStep} className="mt-8 px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-medium shadow-lg shadow-indigo-900/20 flex items-center gap-2">
                        Next: Typical Floor <ArrowRight size={18} />
                    </button>
                )}
            </div>
        );
    }

    // 2. Upload Typical Floor
    if (step === 1) {
        return (
            <div className="flex flex-col items-center justify-center w-full h-full p-8 pb-40 animate-in fade-in">
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-zinc-100">Upload Typical Floor Plan</h2>
                    <p className="text-stone-500">This plan will be repeated for the upper floors.</p>
                </div>

                <div className="relative w-full max-w-lg aspect-video border-2 border-dashed border-stone-700 bg-stone-900/30 rounded-2xl flex flex-col items-center justify-center hover:border-indigo-500/50 transition-colors group">
                    {data.typicalFloor ? (
                        <img src={data.typicalFloor} alt="Typical Floor" className="w-full h-full object-contain p-2" />
                    ) : (
                        <>
                            <Layers className="w-12 h-12 text-stone-600 group-hover:text-stone-400 mb-4 transition-colors" />
                            <p className="text-stone-500 text-sm">Drag & drop or Click using Ground Floor</p>
                        </>
                    )}
                    <input
                        type="file"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        onChange={(e) => handleFileUpload(e, 'typicalFloor')}
                        accept="image/*"
                    />
                </div>

                <div className="flex gap-4 mt-8">
                    <button
                        onClick={() => updateTaskData({ typicalFloor: data.groundFloor })}
                        className="px-6 py-3 bg-stone-800 hover:bg-stone-700 text-stone-300 rounded-xl font-medium border border-stone-700"
                    >
                        Use Ground Floor
                    </button>
                    {data.typicalFloor && (
                        <button onClick={nextStep} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-medium shadow-lg shadow-indigo-900/20 flex items-center gap-2">
                            Next: Specs <ArrowRight size={18} />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // 3. Building Specs & Generate
    if (step === 2 || step === 3) {
        return (
            <div className="flex flex-col items-center justify-center w-full h-full p-8 pb-40 animate-in fade-in">
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-zinc-100">Building Capabilities</h2>
                    <p className="text-stone-500">Define the vertical structure.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-3xl">
                    <div className="bg-stone-900/50 border border-stone-800 p-6 rounded-2xl flex flex-col gap-4">
                        <div className="flex items-center gap-2 text-indigo-400 mb-2">
                            <Ruler size={20} />
                            <h3 className="font-bold">Ground Floor</h3>
                        </div>
                        <label className="text-xs text-stone-500">Height (meters)</label>
                        <input
                            type="number"
                            defaultValue={data.groundFloorHeight || 4}
                            onChange={(e) => handleSpecChange('groundFloorHeight', e.target.value)}
                            className="bg-black/40 border border-stone-700 rounded-lg p-3 text-white focus:outline-none focus:border-indigo-500"
                        />
                    </div>

                    <div className="bg-stone-900/50 border border-stone-800 p-6 rounded-2xl flex flex-col gap-4">
                        <div className="flex items-center gap-2 text-indigo-400 mb-2">
                            <Layers size={20} />
                            <h3 className="font-bold">Typical Floors</h3>
                        </div>
                        <label className="text-xs text-stone-500">Count</label>
                        <input
                            type="number"
                            defaultValue={data.floorsCount || 2}
                            onChange={(e) => handleSpecChange('floorsCount', e.target.value)}
                            className="bg-black/40 border border-stone-700 rounded-lg p-3 text-white focus:outline-none focus:border-indigo-500"
                        />
                        <label className="text-xs text-stone-500">Height (meters)</label>
                        <input
                            type="number"
                            defaultValue={data.typicalFloorHeight || 3.5}
                            onChange={(e) => handleSpecChange('typicalFloorHeight', e.target.value)}
                            className="bg-black/40 border border-stone-700 rounded-lg p-3 text-white focus:outline-none focus:border-indigo-500"
                        />
                    </div>
                </div>

                <div className="mt-12 flex flex-col items-center gap-4">
                    <button
                        onClick={() => generate()}
                        className="px-10 py-5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-2xl font-bold text-xl shadow-xl shadow-indigo-900/30 flex items-center gap-3 transform transition-all hover:scale-105 active:scale-95"
                    >
                        <Sparkles size={24} className="animate-pulse" /> Generate Elevation
                    </button>
                    <p className="text-stone-500 text-sm">Estimated time: 10-15 seconds</p>
                </div>

            </div>
        );
    }

    return null;
};

export default PlanToElevationWizard;
